import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import moment from 'moment/moment';
import classnames from "classnames";
import {useNotificationsContext} from '../../contexts/NotificationsContext';
import FilterSelect from '../../../_common/components/FilterSelect';
import FilterText from '../../../_common/components/FilterText';
import {FilterRangePicker} from '../../../_common/components/FieldDatepicker';
import ButtonAction from '../../../_common/components/ButtonAction';
import ClearIcon from '../../../_common/components/ClearIcon';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {getListForExcel} from '../../actions/notificationsActions';
import {generateExcelFile} from '../../../_common/utils/excel';
import {columnNamesNotifications} from '../../../_common/constants/excelColumnNames';
import {notificationStatusTypeOptions} from "../../../_common/constants/options";
import {MergeCell, ExcelCellValue, PreparedExcelRow, WorkSheet} from "../../../_common/types";
import {NotificationRow} from "../../types";
import styles from '../../../_common/styles/filtersBar.module.scss';

export function NotificationsFilters() {
  const context = useNotificationsContext();
  const contextProps = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams,
    };
  }, [context]);
  const [loading, setLoading] = useState(false);

  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const prefix = 'notifications-filter';
  const datepickerLabel = formatMessage({id: `filter-placeholder-create-date-from-to`, defaultMessage: 'Create date'});

  const qs = new URLSearchParams(window.location.search);
  let fromDate = qs.get('fromDate') || '';
  let toDate = qs.get('toDate') || '';
  let docID = qs.get('docID') || '';
  let subjectID = qs.get('subjectID') || '';
  let statusID = qs.get('statusID') || '';

  const handleExport = (): void => {
    const merge: MergeCell[] = [{s: {r: 1, c: 7}, e: {r: 1, c: 12}}];

    const customCell = (ws: WorkSheet): ExcelCellValue => {
      return (ws['H2'] = {
        t: 's',
        v: '',
        s: {
          alignment: {horizontal: 'center'},
          font: {bold: true},
        },
      });
    };

    setLoading(true);
    dispatch(getListForExcel(contextProps.queryParams))
      // @ts-ignore
      .then(prepareDataForExcel)
      .then((data: PreparedExcelRow[]) => {
        generateExcelFile(
          data,
          'Notifications.xlsx',
          'מרכז התראות',
          merge,
          customCell
        );
      })
      .catch((error: unknown) => {
        console.error(`Error: + ${error}`);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = (data: NotificationRow[]) => {
    return data.map((item, index) => {
      const {
        sentAt,
        docID,
        senderFirstName,
        senderLastName,
        receiverFullName,
        typeID,
        subjectName,
        target,
        statusID,
        statusText,
      } = item;

      const getType = () => {
        switch (typeID) {
          case 1:
            return formatMessage({id: 'notifications-type-email', defaultMessage: 'Email'})
          case 2:
            return formatMessage({id: 'notifications-type-sms', defaultMessage: 'SMS'})
          case 3:
            return formatMessage({id: 'notifications-type-whatsapp', defaultMessage: 'Whatsapp'})
          default:
            return ''
        }
      }
      const getStatus = () => {
        switch (statusID) {
          case 1:
            return formatMessage({id: 'notifications-status-type-pending', defaultMessage: 'Pending'})
          case 2:
            return formatMessage({id: 'notifications-status-type-fail', defaultMessage: 'Fail'})
          case 3:
            return formatMessage({id: 'notifications-status-type-success', defaultMessage: 'Success'})
          default:
            return ''
        }
      };


      return {
        [columnNamesNotifications[0]]: index + 1,
        [columnNamesNotifications[1]]: sentAt ? moment.utc(sentAt).local().format('DD.MM.YYYY - HH:mm:ss') : '',
        [columnNamesNotifications[2]]: subjectName || '',
        [columnNamesNotifications[3]]: [senderFirstName, senderLastName].filter(Boolean).join(' '),
        [columnNamesNotifications[4]]: getType(),
        [columnNamesNotifications[5]]: [receiverFullName, target].filter(Boolean).join(' - '),
        [columnNamesNotifications[6]]: docID || '',
        [columnNamesNotifications[7]]: [getStatus(), statusText].filter(Boolean).join(' - ')
      };
    });
  };

  return (
    <Formik
      initialValues={{
        fromDate,
        toDate,
        docID,
        subjectID,
        statusID
      }}
      onSubmit={(values) => applyFilter(values, contextProps)}
      enableReinitialize={true}
    >
      {({values, handleBlur, handleSubmit, setFieldValue}) => (
        <form onSubmit={handleSubmit} className={styles.container}>
          <div className={classnames(styles.filtersContainer, 'justify-content-start')}>
            <div className={classnames(styles.filter, 'w-350px')}>
              <FilterRangePicker
                onChange={(fromDate, toDate) => {
                  setFieldValue('fromDate', fromDate || '');
                  setFieldValue('toDate', toDate || '');
                  handleSubmit();
                }}
                placeholder={[datepickerLabel]}
                startDate={fromDate}
                endDate={toDate}
                label={fromDate || toDate ? datepickerLabel : ''}
              />
              <ClearIcon
                onClick={() => {
                  setFieldValue('fromDate', '');
                  setFieldValue('toDate', '');
                  handleSubmit();
                }}
              />
            </div>
            <FilterSelect
              fieldName={'subjectID'}
              valueName={values.subjectID}
              options={context.notificationsSubjects as unknown[] as never[]}
              placeholder={formatMessage({id: `${prefix}-subjectID`, defaultMessage: 'Subject'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              disabled={false}
              onChange={false}
              onClear={false}
            />
            <FilterSelect
              fieldName={'statusID'}
              valueName={values.statusID}
              options={notificationStatusTypeOptions as unknown[] as never[]}
              placeholder={formatMessage({id: `${prefix}-statusID`, defaultMessage: 'Status'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              disabled={false}
              onChange={false}
              onClear={false}
            />
            <FilterText
              fieldName={'docID'}
              value={values.docID}
              placeholder={formatMessage({id: `${prefix}-docID`, defaultMessage: 'Document'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              formatValue={false}
            />
          </div>
          <div className={styles.buttonsContainer}>
            <ButtonAction
              variant={'success'}
              label={
                <>
                  {loading
                    ? (
                      <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                    )
                    : (
                      <>
                        <img
                          src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                          alt="export" className={'mr-2'}
                        />
                        <span>{formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                      </>
                    )
                  }
                </>
              }
              onClick={handleExport}
              customClassnames={'w-100px'}
            />
          </div>
        </form>
      )}
    </Formik>
  );
}

export default NotificationsFilters;
